import { BILLING_CONSTANTS } from '@constants'
import { useStore } from '@hooks/useStore'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import 'dayjs/locale/nl'
import { useLocation, useNavigate } from 'react-router-dom'

const FreeAccountStatus: React.FC = observer(() => {
  const { billing } = useStore()
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year')
  const [isUpgrading, setIsUpgrading] = useState(false)

  useEffect(() => {
    billing.fetchPlans()
  }, [])

  const selectedPlan = billing.plans?.find(p => p.interval === billingInterval)

  const handleUpgrade = async () => {
    if (!selectedPlan) return

    setIsUpgrading(true)
    try {
      const checkoutUrl = await billing.subscribe(selectedPlan.stripe_price_id)
      window.location.href = checkoutUrl
    } catch (error: any) {
      toast.error('Er is een fout opgetreden bij het aanmaken van de checkout sessie')
      setIsUpgrading(false)
    }
  }

  const isLoadingPlans = billing.loading && !billing.plans?.length
  const hasError = billing.error && !billing.plans?.length

  return (
    <Card>
      <CardHeader
        title="Abonnementsstatus"
        subheader="Gratis Account"
      />
      <CardContent>
        <Alert
          severity="info"
          sx={{ mb: 3 }}>
          <AlertTitle>Gratis Tier</AlertTitle>
          Je gebruikt de gratis tier met {billing.limits?.monthly_documents || 3} documenten
          per maand. Upgrade naar PRO voor onbeperkte documenten, e-mail verzending en meer.
        </Alert>

        {hasError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}>
            <AlertTitle>Fout bij laden van abonnementen</AlertTitle>
            {billing.error}
          </Alert>
        )}

        <Typography
          variant="h6"
          gutterBottom
          sx={{ mt: 3 }}>
          {BILLING_CONSTANTS.LABELS.CHOOSE_PLAN}
        </Typography>

        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={(e, value) => value && setBillingInterval(value)}
          fullWidth
          color="primary"
          sx={{ mb: 3 }}
          disabled={isLoadingPlans}>
          <ToggleButton value="month">{BILLING_CONSTANTS.MESSAGES.PRICE_MONTHLY}</ToggleButton>
          <ToggleButton
            value="year"
            sx={{ flexDirection: 'column' }}>
            <Typography>{BILLING_CONSTANTS.MESSAGES.PRICE_YEARLY}</Typography>
            <Typography
              variant="caption"
              color="GrayText"
              sx={{ display: 'block' }}>
              {BILLING_CONSTANTS.MESSAGES.SAVINGS_LABEL}
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleUpgrade}
            disabled={isLoadingPlans || isUpgrading || !selectedPlan}>
            {isUpgrading ? 'Bezig met laden...' : BILLING_CONSTANTS.MESSAGES.UPGRADE_CTA}
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
          Betaling start direct - upgrade of downgrade op elk moment
        </Typography>
      </CardContent>
    </Card>
  )
})

const CurrentSubscription: React.FC = observer(() => {
  const { billing, ui } = useStore()

  if (!billing.isPro) return null

  const handleCancel = async () => {
    const formattedTrialEndsAt = billing.status?.trial_ends_at
      ? dayjs(billing.status.trial_ends_at).locale('nl').format('DD MMM YYYY')
      : null
    const formattedCurrentPeriodEnd = billing.status?.current_period_end
      ? dayjs(billing.status.current_period_end).locale('nl').format('DD MMM YYYY')
      : null

    const confirmMessage =
      billing.isOnTrial && formattedTrialEndsAt
        ? BILLING_CONSTANTS.MESSAGES.CANCEL_CONFIRMATION_TRIAL(formattedTrialEndsAt)
        : formattedCurrentPeriodEnd
        ? BILLING_CONSTANTS.MESSAGES.CANCEL_CONFIRMATION_PAID(formattedCurrentPeriodEnd)
        : ''

    try {
      await ui.confirm({
        title: BILLING_CONSTANTS.MESSAGES.CANCEL_CONFIRMATION_TITLE,
        content: `${confirmMessage}\n\n${BILLING_CONSTANTS.MESSAGES.CANCEL_CONFIRMATION_NOTE}`,
        withCancel: true,
      })

      const result = await billing.cancelSubscription()
      const formattedDate = dayjs(result.access_until).locale('nl').format('DD MMM YYYY')
      toast.success(BILLING_CONSTANTS.MESSAGES.CANCEL_SUCCESS(formattedDate))
    } catch (error: any) {
      // User cancelled or error occurred
      if (error.message) {
        toast.error(error.message)
      }
    }
  }

  const handleResume = async () => {
    try {
      await billing.resumeSubscription()
      toast.success('Je abonnement is hervat')
    } catch (error: any) {
      toast.error(error.message || 'Er is een fout opgetreden bij het hervatten van je abonnement')
    }
  }

  const handleManagePayment = async () => {
    try {
      const url = await billing.getPortalUrl()
      window.location.href = url
    } catch (error: any) {
      toast.error('Er is een fout opgetreden bij het openen van het klantportaal')
    }
  }

  const getBillingInterval = () => {
    if (billing.status?.billing_interval === 'year') return BILLING_CONSTANTS.MESSAGES.YEARLY_LABEL
    return BILLING_CONSTANTS.MESSAGES.MONTHLY_LABEL
  }

  const getPrice = () => {
    if (billing.status?.billing_interval === 'year') return '€ 99'
    return '€ 8,99'
  }

  const getPriceWithInterval = () => {
    if (billing.status?.billing_interval === 'year') return '€ 99/jaar'
    return '€ 8,99/maand'
  }

  // Format dates for display
  const formattedStartedAt = billing.status?.started_at
    ? dayjs(billing.status.started_at).locale('nl').format('DD MMM YYYY')
    : null
  const formattedTrialEndsAt = billing.status?.trial_ends_at
    ? dayjs(billing.status.trial_ends_at).locale('nl').format('DD MMM YYYY')
    : null
  const formattedEndsAt = billing.status?.ends_at
    ? dayjs(billing.status.ends_at).locale('nl').format('DD MMM YYYY')
    : null

  return (
    <Card>
      <CardHeader
        title={
          billing.isOnTrial
            ? BILLING_CONSTANTS.MESSAGES.PRO_TRIAL_TITLE
            : BILLING_CONSTANTS.MESSAGES.PRO_SUBSCRIPTION_TITLE
        }
        subheader={
          billing.isOnTrial && billing.status?.trial_days_remaining
            ? BILLING_CONSTANTS.MESSAGES.TRIAL_ENDS_AT(String(billing.status.trial_days_remaining))
            : formattedStartedAt
            ? BILLING_CONSTANTS.MESSAGES.ACTIVE_SINCE(formattedStartedAt)
            : undefined
        }
      />
      <CardContent>
        {billing.isOnTrial && formattedTrialEndsAt && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}>
            <AlertTitle>
              Proefperiode actief - {billing.status?.trial_days_remaining}{' '}
              {billing.status?.trial_days_remaining === 1 ? 'dag' : 'dagen'} resterend
            </AlertTitle>
            {BILLING_CONSTANTS.MESSAGES.TRIAL_AUTO_CHARGE(formattedTrialEndsAt, getPrice(), getBillingInterval())}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1">{BILLING_CONSTANTS.MESSAGES.STATUS_LABEL}</Typography>
          <Chip
            label={
              billing.isOnTrial && billing.status?.trial_days_remaining
                ? `Proefperiode (${billing.status.trial_days_remaining} ${
                    billing.status.trial_days_remaining === 1 ? 'dag' : 'dagen'
                  } resterend)`
                : billing.isOnTrial
                ? 'Proefperiode'
                : 'Actief'
            }
            color={billing.isOnTrial ? 'info' : 'success'}
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1">{BILLING_CONSTANTS.MESSAGES.PLAN_LABEL}</Typography>
          <Typography
            variant="body1"
            fontWeight="bold">
            PRO {getBillingInterval()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1">{BILLING_CONSTANTS.MESSAGES.PRICE_LABEL}</Typography>
          <Typography
            variant="body1"
            fontWeight="bold">
            {getPriceWithInterval()}
          </Typography>
        </Box>

        {formattedEndsAt && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}>
            <AlertTitle>Abonnement geannuleerd</AlertTitle>
            {billing.status?.returning_to_free_tier
              ? `Je hebt PRO toegang tot ${formattedEndsAt}. ` +
                'Daarna keer je terug naar de gratis tier (3 documenten per maand).'
              : BILLING_CONSTANTS.MESSAGES.SUBSCRIPTION_ENDING(formattedEndsAt)}
            {billing.status?.is_on_grace_period &&
              !billing.status?.returning_to_free_tier &&
              ` ${BILLING_CONSTANTS.MESSAGES.GRACE_PERIOD_NOTE}`}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          {!billing.status?.ends_at ? (
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}>
              {BILLING_CONSTANTS.MESSAGES.CANCEL_SUBSCRIPTION}
            </Button>
          ) : billing.status?.is_on_grace_period ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleResume}>
              {BILLING_CONSTANTS.MESSAGES.RESUME_SUBSCRIPTION}
            </Button>
          ) : null}
          <Button
            variant="outlined"
            onClick={handleManagePayment}>
            {BILLING_CONSTANTS.MESSAGES.MANAGE_PAYMENT}
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
})

const UsageAnalytics: React.FC = observer(() => {
  const { billing } = useStore()

  return (
    <Card>
      <CardHeader
        title={BILLING_CONSTANTS.LABELS.USAGE_STATS}
        subheader={BILLING_CONSTANTS.LABELS.USAGE_PERIOD}
      />
      <CardContent>
        {billing.isPro ? (
          // PRO users: Show only "Unlimited" label
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom>
              {BILLING_CONSTANTS.LABELS.UNLIMITED_DOCUMENTS}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary">
              Geen limiet op het aantal documenten
            </Typography>
          </Box>
        ) : (
          // Free users: Show document count
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">{BILLING_CONSTANTS.LABELS.DOCUMENTS_SENT}</Typography>
              <Typography
                variant="h6"
                color="primary">
                {billing.monthlyDocumentsFinalized}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary">
              {BILLING_CONSTANTS.LABELS.UPGRADE_TO_SEND}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  )
})

const InvoiceHistory: React.FC = observer(() => {
  const { billing } = useStore()

  useEffect(() => {
    billing.fetchInvoices()
  }, [])

  if (!billing.invoices.length) return null

  return (
    <Card>
      <CardHeader title="Factuurgeschiedenis" />
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nummer</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell>Bedrag</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billing.invoices.map(invoice => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.number}</TableCell>
                  <TableCell>{new Date(invoice.created * 1000).toLocaleDateString('nl-NL')}</TableCell>
                  <TableCell>€ {(invoice.amount_paid / 100).toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.status === 'paid' ? 'Betaald' : invoice.status}
                      size="small"
                      color="success"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      component="a"
                      href={invoice.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer">
                      Download PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
})

export const BillingSettingsForm: React.FC = observer(() => {
  const { billing } = useStore()
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const loadStatus = async () => {
      await billing.fetchStatus()
      setInitialLoadComplete(true)

      // Check for checkout success
      const params = new URLSearchParams(location.search)
      if (params.get('success') === 'true') {
        // Show success message
        toast.success('Je bent nu geüpgraded naar PRO! Je hebt nu direct toegang tot alle PRO functies.')

        // Remove success parameter from URL without reloading
        params.delete('success')
        const newSearch = params.toString()
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash}`
        navigate(newUrl, { replace: true })
      }
    }
    loadStatus()
  }, [])

  // Show loader only during initial load to prevent flash
  if (!initialLoadComplete) {
    return (
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography
                variant="body2"
                color="text.secondary">
                Abonnementsstatus laden...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      {billing.isPro ? (
        <>
          <CurrentSubscription />
          <InvoiceHistory />
        </>
      ) : (
        <FreeAccountStatus />
      )}
      <UsageAnalytics />
    </Stack>
  )
})
